import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight, Check, ChevronDown, ClipboardPlus, Copy, FileUp, Inbox,
  Link2, CalendarClock, Paperclip, Plus, QrCode, Send, Stethoscope,
  StickyNote, UserRoundPlus,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import {
  departmentLabel,
  type IntakeCase,
  type IntakeCaseView,
  type IntakeDocType,
  type IntakePatientData,
  type IntakeToken,
} from '@refdash/api-client';
import { DateField } from '@refdash/ui';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api';
import { formatDocumentSize, validateIntakeDocuments } from '@/lib/intake-documents';
import { loadIntakeLang, saveIntakeLang, t, type Lang } from '@/lib/intake-i18n';
import { SpecialtyPicker } from '@/components/specialty-picker';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';

type Priority = 'routine' | 'urgent' | 'emergency';

const selectClass = 'h-10 w-full rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-primary';
const textareaClass = 'min-h-24 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary';

export { DOC_TYPE_LABELS } from '@/lib/case-labels';
import { DOC_TYPE_LABELS, IDENTITY_TYPE_LABELS, PAYMENT_METHOD_LABELS } from '@/lib/case-labels';

const VIEWS: Array<{ key: IntakeCaseView; label: string }> = [
  { key: 'triage', label: 'Triage' },
  { key: 'review', label: 'In review' },
  { key: 'ready', label: 'Ready for GlobeMed' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'declined', label: 'Declined' },
];

// ── Page ─────────────────────────────────────────────────────────────

export function IntakePage() {
  const { user } = useAuth();
  const hospitalId = user?.hospital_id ?? undefined;
  const qc = useQueryClient();
  const [view, setView] = useState<IntakeCaseView>('triage');
  const [addOpen, setAddOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);

  // Every intake-staff role can create cases; managing the public form
  // availability lives in Settings → Intake (rep only).
  const canCreate = ['hospital_rep', 'coordinator', 'intake_coordinator', 'operator', 'admin']
    .includes(user?.role ?? '');

  const [limit, setLimit] = useState(50);

  const settings = useQuery({
    queryKey: ['intake-settings', hospitalId],
    queryFn: () => api.intake.getSettings(hospitalId!),
    enabled: !!hospitalId,
  });
  const enabled = settings.data?.intake_enabled === true;
  // Only the ACTIVE tab loads rows — archives can grow unbounded, so tab
  // badges come from a lightweight counts endpoint instead of five full lists.
  const counts = useQuery({
    queryKey: ['intake-counts', hospitalId],
    queryFn: () => api.intake.countCases(hospitalId!),
    enabled: !!hospitalId && enabled,
    refetchInterval: 30_000,
  });
  const active = useQuery({
    queryKey: ['intake-cases', hospitalId, view, limit],
    queryFn: () => api.intake.listCases(hospitalId!, undefined, view, { limit }),
    enabled: !!hospitalId && enabled,
    refetchInterval: 30_000,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['intake-cases'] });
    qc.invalidateQueries({ queryKey: ['intake-counts'] });
    qc.invalidateQueries({ queryKey: ['approvals'] });
  };
  const switchView = (next: IntakeCaseView) => { setView(next); setLimit(50); };

  if (!hospitalId) return null;

  return (
    <div className="px-4 py-5 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-5xl space-y-5">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-sans text-2xl font-black tracking-tight text-foreground">Intake</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Direct cases from staff, invited patients, and the public form.
            </p>
          </div>
          {canCreate && enabled && (
            <div className="flex items-center gap-2">
              <Button variant="outline" className="gap-2" onClick={() => setLinkOpen(true)}>
                <QrCode className="h-4 w-4" /> Public link
              </Button>
              <Button className="gap-2" onClick={() => setAddOpen(true)}>
                <Plus className="h-4 w-4" /> Add case
              </Button>
            </div>
          )}
        </header>

        {settings.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : !enabled ? (
          <Card className="border-dashed p-8 text-center">
            <Stethoscope className="mx-auto h-7 w-7 text-muted-foreground" />
            <h2 className="mt-3 font-bold">Direct Intake is not enabled</h2>
            <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
              A RefDash operator must enable this product capability for {settings.data?.hospital_name ?? 'your hospital'}.
            </p>
          </Card>
        ) : (
          <>
            {/* Work-queue tabs */}
            <div className="flex flex-wrap gap-1.5">
              {VIEWS.map(({ key, label }) => {
                const count = counts.data?.[key];
                return (
                  <button
                    key={key}
                    onClick={() => switchView(key)}
                    className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                      view === key
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-secondary text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {label}
                    {typeof count === 'number' && count > 0 && (
                      <span className={`ml-1.5 tabular-nums ${view === key ? 'text-primary-foreground/80' : ''}`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* The list */}
            {active.isLoading ? (
              <Skeleton className="h-28 w-full" />
            ) : (active.data ?? []).length === 0 ? (
              <Card className="flex flex-col items-center gap-2 border-dashed py-10 text-center">
                <Inbox className="h-6 w-6 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {view === 'triage' ? 'No cases waiting for triage.'
                    : view === 'ready' ? 'No accepted cases awaiting GlobeMed submission.'
                    : 'Nothing here yet.'}
                </p>
              </Card>
            ) : (
              <div className="space-y-3">
                {(active.data ?? []).map((c) => (
                  <CaseRow key={c.id} c={c} view={view} hospitalId={hospitalId} onChanged={refresh} />
                ))}
                {(active.data ?? []).length >= limit && (
                  <Button variant="outline" className="w-full" onClick={() => setLimit((l) => l + 50)}>
                    Load more
                  </Button>
                )}
              </div>
            )}

            {canCreate && <PatientInvitations hospitalId={hospitalId} />}
          </>
        )}

        <AddCaseDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          hospitalId={hospitalId}
          onCreated={refresh}
        />
        <PublicLinkDialog
          open={linkOpen}
          onOpenChange={setLinkOpen}
          hospitalId={hospitalId}
          publicEnabled={Boolean(settings.data?.public_intake_enabled)}
          isRep={user?.role === 'hospital_rep'}
        />
      </div>
    </div>
  );
}

// ── Case rows ────────────────────────────────────────────────────────

function parseInfo(c: IntakeCase): Record<string, unknown> {
  return typeof c.patient_info === 'string' ? JSON.parse(c.patient_info) : c.patient_info;
}

const PRIORITY_STYLE: Record<string, string> = {
  urgent: 'bg-amber-100 text-amber-800',
  emergency: 'bg-red-100 text-red-700',
};

function CaseRow({ c, view, hospitalId, onChanged }: {
  c: IntakeCase; view: IntakeCaseView; hospitalId: string; onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const info = parseInfo(c);
  const overdue = c.followup_at && new Date(c.followup_at).getTime() <= Date.now();

  return (
    <Card className="overflow-hidden p-0">
      <button
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/40"
        onClick={() => setOpen((o) => !o)}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-bold">{String(info?.name ?? info?.patient_name ?? 'Unknown patient')}</p>
            {c.priority && c.priority !== 'routine' && (
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${PRIORITY_STYLE[c.priority] ?? ''}`}>
                {c.priority}
              </span>
            )}
            {c.followup_at && (
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                overdue ? 'bg-red-100 text-red-700' : 'bg-secondary text-muted-foreground'
              }`}>
                <CalendarClock className="h-3 w-3" />
                Follow up {new Date(c.followup_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
              </span>
            )}
          </div>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            <span className="font-mono">{c.ihalaty_reference}</span>
            {' · '}{c.intake_source?.replace('_', ' ')}
            {c.department_code && <>{' · '}{c.department_code} — {c.department_name}</>}
            {c.attachments.length > 0 && <>{' · '}{c.attachments.length} document{c.attachments.length === 1 ? '' : 's'}</>}
            {c.notes.length > 0 && <>{' · '}{c.notes.length} note{c.notes.length === 1 ? '' : 's'}</>}
          </p>
        </div>
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="border-t border-border px-4 py-4">
          <CaseDetail c={c} info={info} view={view} hospitalId={hospitalId} onChanged={onChanged} />
        </div>
      )}
    </Card>
  );
}

function CaseDetail({ c, info, view, hospitalId, onChanged }: {
  c: IntakeCase; info: Record<string, unknown>; view: IntakeCaseView;
  hospitalId: string; onChanged: () => void;
}) {
  return (
    <div className="space-y-4">
      {/* Patient facts — one stable shape on every tab: absent values render
          as an em-dash instead of silently dropping the row, so cards no
          longer look different between views/origins. */}
      <div className="grid gap-x-5 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
        {([
          ['Phone', info?.phone],
          ['Identity', info?.national_id
            ? `${info.national_id}${info?.identity_type ? ` (${IDENTITY_TYPE_LABELS[String(info.identity_type)] ?? info.identity_type})` : ''}`
            : null],
          ['Nationality', info?.nationality],
          ['Payment method', info?.payment_method ? PAYMENT_METHOD_LABELS[String(info.payment_method)] ?? info.payment_method : null],
          ['Requested specialty', info?.required_specialty],
          ['Source provider', info?.source_provider],
          ['Referral type', info?.ref_type],
          ['Bed type', info?.bed_type],
        ] as [string, unknown][]).map(([label, v]) => (
          <div key={label}>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
            <p className={v ? 'font-medium' : 'font-medium text-muted-foreground/50'}>{v ? String(v) : '—'}</p>
          </div>
        ))}
      </div>
      {Boolean(info?.referral_cause) && (
        <p className="rounded-lg bg-secondary/40 p-3 text-sm text-muted-foreground">{String(info.referral_cause)}</p>
      )}

      {/* Documents, tagged */}
      {c.attachments.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {c.attachments.map((a) => (
            <a
              key={a.id}
              href={api.approvals.getAttachmentUrl(a.id, a.filename)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-1 text-xs font-semibold hover:border-primary/40 hover:text-primary"
            >
              <Paperclip className="h-3 w-3 shrink-0" />
              <span className="rounded bg-secondary px-1 py-0.5 text-[10px] font-bold text-muted-foreground">
                {DOC_TYPE_LABELS[a.attachment_type] ?? a.attachment_type}
              </span>
              <span className="truncate">{a.filename}</span>
            </a>
          ))}
        </div>
      )}

      {/* Per-stage actions */}
      {view === 'triage' && <TriageActions c={c} hospitalId={hospitalId} onDone={onChanged} />}
      {view === 'review' && (
        <p className="rounded-lg border border-border bg-secondary/20 p-3 text-xs text-muted-foreground">
          Routed to {c.department_code ? `${c.department_code} — ${c.department_name}` : 'a department'} —
          awaiting the clinical decision in <Link to="/approvals" className="font-semibold text-primary hover:underline">Approvals</Link>.
        </p>
      )}
      {view === 'ready' && <ReadyActions c={c} onDone={onChanged} />}
      {view === 'submitted' && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
          <p className="text-xs text-emerald-800">
            Submitted to GlobeMed {c.globemed_submitted_at && `on ${new Date(c.globemed_submitted_at).toLocaleString('en-GB')}`}
            {c.globemed_submitted_by && ` by ${c.globemed_submitted_by}`}
            {c.globemed_reference && <> · reference <span className="font-mono font-semibold">{c.globemed_reference}</span></>}
          </p>
          <div className="mt-2">
            <ReissueLetterButton c={c} onDone={onChanged} />
          </div>
        </div>
      )}
      {view === 'declined' && (
        <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800">
          Declined{c.decided_at && ` on ${new Date(c.decided_at).toLocaleString('en-GB')}`}
          {c.decided_by_name && ` by ${c.decided_by_name}`}
          {c.decision_reason && <> — <span className="font-semibold">{c.decision_reason}</span></>}
        </p>
      )}

      {/* Notes + follow-up (every stage) */}
      <NotesSection c={c} onChanged={onChanged} />
    </div>
  );
}

function TriageActions({ c, hospitalId, onDone }: { c: IntakeCase; hospitalId: string; onDone: () => void }) {
  const [departmentId, setDepartmentId] = useState<number | ''>('');
  const [priority, setPriority] = useState<Priority>('routine');
  const depts = useQuery({ queryKey: ['departments', hospitalId], queryFn: () => api.hospitals.listDepartments(hospitalId) });
  const triage = useMutation({
    mutationFn: () => api.intake.triage(c.id, { department_id: Number(departmentId), priority }),
    onSuccess: () => { toast.success('Routed to review'); onDone(); },
    onError: (error: Error) => toast.error(error.message),
  });
  return (
    <div className="grid gap-2 sm:grid-cols-[1fr_0.7fr_auto]">
      <select className={selectClass} value={departmentId} onChange={(e) => setDepartmentId(e.target.value ? Number(e.target.value) : '')}>
        <option value="">Select department</option>
        {(depts.data ?? []).map((d) => <option key={d.id} value={d.id}>{departmentLabel(d)}</option>)}
      </select>
      <select className={selectClass} value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
        <option value="routine">Routine</option><option value="urgent">Urgent</option><option value="emergency">Emergency</option>
      </select>
      <Button className="gap-2" onClick={() => triage.mutate()} disabled={!departmentId || triage.isPending}>
        {triage.isPending ? 'Routing…' : 'Route to review'} <ArrowRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

/** The MOH letter is only valid 48h — re-issuing replaces it with a copy
 *  dated today. Shown on both the Ready and Submitted stages, so old cases
 *  accepted before letters existed can get one too. */
function ReissueLetterButton({ c, onDone }: { c: IntakeCase; onDone: () => void }) {
  const letter = useMutation({
    mutationFn: () => api.intake.regenerateAcceptanceLetter(c.id),
    onSuccess: () => { toast.success('Acceptance letter re-issued with today’s date'); onDone(); },
    onError: (error: Error) => toast.error(error.message),
  });
  return (
    <Button variant="outline" className="gap-2" onClick={() => letter.mutate()} disabled={letter.isPending}>
      <Paperclip className="h-4 w-4" /> {letter.isPending ? 'Issuing…' : 'Re-issue acceptance letter'}
    </Button>
  );
}

function ReadyActions({ c, onDone }: { c: IntakeCase; onDone: () => void }) {
  const [reference, setReference] = useState('');
  const submit = useMutation({
    mutationFn: () => api.intake.markGlobemedSubmitted(c.id, reference.trim() || undefined),
    onSuccess: () => { toast.success('Marked as submitted to GlobeMed'); onDone(); },
    onError: (error: Error) => toast.error(error.message),
  });
  return (
    <div className="rounded-xl border border-primary/20 bg-primary/5 p-3">
      <p className="text-xs font-semibold text-foreground">
        Accepted — submit this case to GlobeMed, then record the hand-off here.
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        <Input
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="GlobeMed reference (optional)"
          className="max-w-60"
        />
        <Button className="gap-2" onClick={() => submit.mutate()} disabled={submit.isPending}>
          <Send className="h-4 w-4" /> {submit.isPending ? 'Recording…' : 'Mark submitted'}
        </Button>
        <ReissueLetterButton c={c} onDone={onDone} />
      </div>
    </div>
  );
}

function NotesSection({ c, onChanged }: { c: IntakeCase; onChanged: () => void }) {
  const [note, setNote] = useState('');
  const [followup, setFollowup] = useState('');
  const add = useMutation({
    mutationFn: () => api.intake.addNote(c.id, { note: note.trim(), followup_at: followup || null }),
    onSuccess: () => { setNote(''); setFollowup(''); toast.success('Note added'); onChanged(); },
    onError: (error: Error) => toast.error(error.message),
  });
  const clearFollowup = useMutation({
    mutationFn: () => api.intake.setFollowup(c.id, null),
    onSuccess: () => { toast.success('Follow-up cleared'); onChanged(); },
    onError: (error: Error) => toast.error(error.message),
  });
  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          <StickyNote className="h-3.5 w-3.5" /> Follow-up notes
        </p>
        {c.followup_at && (
          <button
            className="text-[11px] font-semibold text-muted-foreground hover:text-foreground"
            onClick={() => clearFollowup.mutate()}
          >
            Clear follow-up
          </button>
        )}
      </div>
      {c.notes.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {c.notes.map((n) => (
            <li key={n.id} className="rounded-lg bg-secondary/30 px-3 py-2 text-xs">
              <span className="text-foreground">{n.note}</span>
              <span className="ml-2 text-muted-foreground">
                — {n.author}, {new Date(n.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                {n.followup_at && ` · follow up ${new Date(n.followup_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}`}
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add a note (e.g. called patient, awaiting report)…"
          className="min-w-48 flex-1"
        />
        <DateField value={followup} onChange={setFollowup} placeholder="Follow-up date" className="w-40" />
        <Button variant="outline" size="sm" onClick={() => add.mutate()} disabled={!note.trim() || add.isPending}>
          Add note
        </Button>
      </div>
    </div>
  );
}

// ── Add-case dialog (create OR invite, bilingual like the patient form) ──

function AddCaseDialog({ open, onOpenChange, hospitalId, onCreated }: {
  open: boolean; onOpenChange: (open: boolean) => void; hospitalId: string; onCreated: () => void;
}) {
  const [mode, setMode] = useState<'choose' | 'create' | 'invite'>('choose');
  const [lang, setLang] = useState<Lang>(loadIntakeLang);
  const close = (value: boolean) => { onOpenChange(value); if (!value) setMode('choose'); };
  const toggleLang = () => {
    const next: Lang = lang === 'ar' ? 'en' : 'ar';
    setLang(next); saveIntakeLang(next);
  };
  const ar = lang === 'ar';
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[88dvh] max-w-2xl overflow-y-auto" dir={ar ? 'rtl' : 'ltr'}>
        <DialogHeader className={ar ? 'text-right sm:text-right' : ''}>
          <div className="flex items-center justify-between gap-3">
            <DialogTitle>{ar ? 'إضافة حالة' : 'Add a case'}</DialogTitle>
            <button
              onClick={toggleLang}
              className="me-6 rounded-full border border-border px-2.5 py-1 text-[11px] font-bold text-muted-foreground hover:text-foreground"
            >
              {t(lang, 'language_toggle')}
            </button>
          </div>
          <DialogDescription>
            {mode === 'choose' && (ar
              ? 'أنشئ ملف الحالة كاملًا بنفسك، أو ادعُ المريض لاستكماله.'
              : 'Create the full case packet yourself, or invite the patient to complete it.')}
            {mode === 'create' && (ar
              ? 'إدخال الموظف — البيانات والمستندات والتوجيه في خطوة واحدة.'
              : 'Staff entry — complete details, documents, and routing in one sitting.')}
            {mode === 'invite' && (ar
              ? 'عبّئ ما تعرفه؛ ويستكمل المريض البيانات والمستندات عبر رابط خاص.'
              : 'Prefill what you know; the patient completes and uploads via a private link.')}
          </DialogDescription>
        </DialogHeader>
        {mode === 'choose' ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <button
              onClick={() => setMode('create')}
              className="rounded-2xl border border-border p-4 text-start transition-all hover:-translate-y-0.5 hover:border-primary/40"
            >
              <ClipboardPlus className="mb-3 h-5 w-5 text-primary" />
              <p className="font-bold">{ar ? 'إنشاء حالة' : 'Create case'}</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                {ar ? 'الملف الكامل الآن: البيانات والمستندات والتوجيه.' : 'Full packet now: details, documents, and routing.'}
              </p>
            </button>
            <button
              onClick={() => setMode('invite')}
              className="rounded-2xl border border-border p-4 text-start transition-all hover:-translate-y-0.5 hover:border-primary/40"
            >
              <UserRoundPlus className="mb-3 h-5 w-5 text-primary" />
              <p className="font-bold">{ar ? 'دعوة المريض' : 'Invite patient'}</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                {ar ? 'أرسل رابطًا خاصًا محدود الصلاحية يستكمله المريض.' : 'Send a private, expiring link the patient finishes.'}
              </p>
            </button>
          </div>
        ) : mode === 'create' ? (
          <StaffCaseForm hospitalId={hospitalId} lang={lang} onCreated={() => { onCreated(); close(false); }} />
        ) : (
          <InvitePatient hospitalId={hospitalId} lang={lang} />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Public link dialog (URL + QR) ────────────────────────────────────

function PublicLinkDialog({ open, onOpenChange, hospitalId, publicEnabled, isRep }: {
  open: boolean; onOpenChange: (open: boolean) => void; hospitalId: string;
  publicEnabled: boolean; isRep: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}${import.meta.env.BASE_URL.replace(/\/$/, '')}/intake/public/${encodeURIComponent(hospitalId)}`;
  const copy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Public referral form</DialogTitle>
          <DialogDescription>
            Share on the hospital website, WhatsApp, or print the QR code for the front desk.
          </DialogDescription>
        </DialogHeader>
        {publicEnabled ? (
          <div className="space-y-4">
            <div className="mx-auto w-fit rounded-2xl border border-border bg-white p-4">
              <QRCodeSVG value={url} size={200} />
            </div>
            <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
              <code className="min-w-0 flex-1 break-all text-xs" dir="ltr">{url}</code>
              <Button variant="outline" size="sm" onClick={copy} className="shrink-0 gap-1.5">
                {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Anyone with this link can submit a case; every submission enters mandatory triage.
              {isRep && <> Availability is managed in <Link to="/settings/intake" className="font-semibold text-primary hover:underline" onClick={() => onOpenChange(false)}>Settings → Intake</Link>.</>}
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            The public form is currently <span className="font-bold">disabled</span>.
            {isRep
              ? <> Enable it in <Link to="/settings/intake" className="font-semibold underline" onClick={() => onOpenChange(false)}>Settings → Intake</Link>.</>
              : ' Ask your hospital rep to enable it from Settings.'}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Staff create + invite forms (shared field grid) ──────────────────

interface CaseFields {
  patientName: string;
  identityType: 'national_id' | 'iqama' | 'passport' | 'other';
  nationalId: string;
  phone: string;
  nationality: string;
  cause: string;
  sourceProvider: string;
  refType: string;
  specialty: string;
  specialtyCode: string | null;
  bedType: string;
  paymentMethod: '' | 'cash' | 'insurance';
}

const emptyFields: CaseFields = {
  patientName: '', identityType: 'national_id', nationalId: '', phone: '', nationality: '',
  cause: '', sourceProvider: '', refType: '', specialty: '', specialtyCode: null, bedType: '',
  paymentMethod: '',
};

interface TaggedFile { file: File; docType: IntakeDocType }

function StaffCaseForm({ hospitalId, lang, onCreated }: { hospitalId: string; lang: Lang; onCreated: () => void }) {
  const [fields, setFields] = useState<CaseFields>(emptyFields);
  const [departmentId, setDepartmentId] = useState<number | ''>('');
  const [priority, setPriority] = useState<Priority>('routine');
  const [consent, setConsent] = useState(false);
  const [files, setFiles] = useState<TaggedFile[]>([]);
  const depts = useQuery({ queryKey: ['departments', hospitalId], queryFn: () => api.hospitals.listDepartments(hospitalId) });

  const create = useMutation({
    mutationFn: async () => {
      const created = await api.intake.createStaffCase({
        hospital_id: hospitalId,
        patient_name: fields.patientName.trim(),
        identity_type: fields.identityType,
        national_id: fields.nationalId || null,
        phone: fields.phone || null,
        nationality: fields.nationality || null,
        referral_cause: fields.cause.trim(),
        source_provider: fields.sourceProvider || null,
        ref_type: fields.refType || null,
        required_specialty: fields.specialty || null,
        required_specialty_code: fields.specialtyCode,
        bed_type: fields.bedType || null,
        payment_method: fields.paymentMethod || null,
        department_id: departmentId || null,
        priority,
        consent_obtained: consent,
      });
      let uploadErrors = 0;
      for (const { file, docType } of files) {
        try { await api.intake.uploadStaffDocument(created.id, file, docType); }
        catch { uploadErrors += 1; }
      }
      return { ...created, uploadErrors };
    },
    onSuccess: (created) => {
      if (created.uploadErrors) toast.warning(`Case ${created.reference} created, but ${created.uploadErrors} document upload${created.uploadErrors === 1 ? '' : 's'} failed`);
      else toast.success(`Case ${created.reference} created`);
      setFields(emptyFields); setDepartmentId(''); setPriority('routine'); setConsent(false); setFiles([]);
      onCreated();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const ready = fields.patientName.trim().length > 1 && fields.cause.trim().length > 1 && consent;
  return (
    <div>
      <CaseFieldGrid fields={fields} onChange={setFields} showRoutingFields lang={lang} />
      <div className="mt-5 grid gap-4 border-t border-border pt-5 sm:grid-cols-2">
        <Field label={t(lang, 'staff_internal_department')}>
          <select className={selectClass} value={departmentId} onChange={(e) => setDepartmentId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">{t(lang, 'staff_leave_for_triage')}</option>
            {(depts.data ?? []).map((d) => <option key={d.id} value={d.id}>{departmentLabel(d)}</option>)}
          </select>
        </Field>
        <Field label={t(lang, 'staff_priority')}>
          <select className={selectClass} value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
            <option value="routine">{t(lang, 'staff_priority_routine')}</option>
            <option value="urgent">{t(lang, 'staff_priority_urgent')}</option>
            <option value="emergency">{t(lang, 'staff_priority_emergency')}</option>
          </select>
        </Field>
      </div>
      <SectionedDocumentPicker files={files} onChange={setFiles} lang={lang} />
      <label className="mt-5 flex items-start gap-3 rounded-xl border border-border bg-secondary/20 p-4 text-sm">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4" />
        <span>{t(lang, 'staff_consent')}</span>
      </label>
      <div className="mt-5 flex justify-end">
        <Button className="gap-2" disabled={!ready || create.isPending} onClick={() => create.mutate()}>
          {create.isPending ? t(lang, 'staff_creating_case') : t(lang, 'staff_create_case')}
          <ArrowRight className={`h-4 w-4 ${lang === 'ar' ? 'rotate-180' : ''}`} />
        </Button>
      </div>
    </div>
  );
}

function InvitePatient({ hospitalId, lang }: { hospitalId: string; lang: Lang }) {
  const qc = useQueryClient();
  const [fields, setFields] = useState<CaseFields>(emptyFields);
  const [ttl, setTtl] = useState('72');
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const mint = useMutation({
    mutationFn: () => api.intake.mintToken({
      hospital_id: hospitalId,
      patient_hint: fields.patientName || null,
      ttl_hours: Number(ttl),
      initial_data: toInitialData(fields),
    }),
    onSuccess: (result) => {
      const path = `${import.meta.env.BASE_URL.replace(/\/$/, '')}${result.submit_path}`;
      setLink(`${window.location.origin}${path}`);
      toast.success('Private patient link created');
      // Surface it in the desk's invitations list straight away, so closing
      // this dialog is no longer the same as losing the link.
      qc.invalidateQueries({ queryKey: ['intake-tokens'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });
  const copy = async () => {
    if (!link) return;
    await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500);
  };
  const hasPrefill = Boolean(
    fields.patientName.trim() || fields.phone.trim() || fields.cause.trim()
    || fields.sourceProvider.trim() || fields.specialty.trim(),
  );
  return (
    <div>
      <CaseFieldGrid fields={fields} onChange={setFields} showRoutingFields lang={lang} />
      <div className="mt-5 max-w-xs">
        <Field label={t(lang, 'staff_invite_expires')}>
          <select className={selectClass} value={ttl} onChange={(e) => setTtl(e.target.value)}>
            <option value="24">{t(lang, 'staff_ttl_24')}</option>
            <option value="72">{t(lang, 'staff_ttl_72')}</option>
            <option value="168">{t(lang, 'staff_ttl_168')}</option>
          </select>
        </Field>
      </div>
      <div className="mt-5 flex justify-end">
        <Button className="gap-2" onClick={() => mint.mutate()} disabled={mint.isPending || !hasPrefill}>
          <Link2 className="h-4 w-4" /> {mint.isPending ? t(lang, 'staff_generating_link') : t(lang, 'staff_generate_link')}
        </Button>
      </div>
      {!hasPrefill && <p className="mt-2 text-end text-xs text-amber-700">{t(lang, 'staff_invite_needs_prefill')}</p>}
      {link && (
        <div className="mt-5 space-y-3">
          <div className="mx-auto w-fit rounded-2xl border border-border bg-white p-3">
            <QRCodeSVG value={link} size={144} />
          </div>
          <div className="flex items-center gap-2 rounded-xl border border-primary/20 bg-primary/5 p-3">
            <code className="min-w-0 flex-1 break-all text-xs" dir="ltr">{link}</code>
            <Button variant="outline" size="sm" onClick={copy} className="shrink-0 gap-1.5">
              {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? t(lang, 'staff_copied') : t(lang, 'staff_copy')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Outstanding patient invitations ──────────────────────────────────
//
// A minted link used to exist only in the browser tab that created it: close
// the dialog and the coordinator could no longer re-send it, see whether the
// patient had used it, or pull one sent to the wrong number — while the link
// itself kept working, carrying a prefilled patient packet.

function relativeExpiry(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const hours = Math.round(ms / 3_600_000);
  if (hours < 1) return `${Math.max(1, Math.round(ms / 60_000))} min left`;
  if (hours < 48) return `${hours}h left`;
  return `${Math.round(hours / 24)}d left`;
}

const TOKEN_BADGE: Record<string, { label: string; className: string }> = {
  active: { label: 'Awaiting patient', className: 'bg-primary/10 text-primary' },
  used: { label: 'Submitted', className: 'bg-emerald-100 text-emerald-800' },
  revoked: { label: 'Revoked', className: 'bg-secondary text-muted-foreground' },
  expired: { label: 'Expired', className: 'bg-secondary text-muted-foreground' },
};

function PatientInvitations({ hospitalId }: { hospitalId: string }) {
  const qc = useQueryClient();
  const [showAll, setShowAll] = useState(false);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [confirmId, setConfirmId] = useState<number | null>(null);

  const tokens = useQuery({
    queryKey: ['intake-tokens', hospitalId],
    queryFn: () => api.intake.listTokens(hospitalId),
    refetchInterval: 60_000,
  });

  const revoke = useMutation({
    mutationFn: (id: number) => api.intake.revokeToken(hospitalId, id),
    onSuccess: () => {
      toast.success('Invitation revoked — the link no longer works');
      setConfirmId(null);
      qc.invalidateQueries({ queryKey: ['intake-tokens'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const all = tokens.data ?? [];
  const live = all.filter((t) => t.status === 'active');
  const shown = showAll ? all : live;

  if (tokens.isLoading || all.length === 0) return null;

  const copy = async (token: IntakeToken) => {
    if (!token.token) return;
    const path = `${import.meta.env.BASE_URL.replace(/\/$/, '')}/intake/submit/${token.token}`;
    await navigator.clipboard.writeText(`${window.location.origin}${path}`);
    setCopiedId(token.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-sans text-sm font-black text-foreground">Patient invitations</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {live.length === 0
              ? 'No links are waiting on a patient.'
              : `${live.length} link${live.length === 1 ? '' : 's'} waiting on a patient.`}
          </p>
        </div>
        {all.length > live.length && (
          <Button variant="ghost" size="sm" onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'Show only open' : `Show all (${all.length})`}
          </Button>
        )}
      </div>

      {shown.length === 0 ? (
        <p className="mt-3 text-xs text-muted-foreground">Nothing open right now.</p>
      ) : (
        <div className="mt-3 space-y-2">
          {shown.map((tk) => {
            const badge = TOKEN_BADGE[tk.status] ?? TOKEN_BADGE.expired;
            return (
              <div
                key={tk.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-border px-3 py-2.5"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground">
                    {tk.patient_hint || 'Unnamed patient'}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {tk.created_by ? `by ${tk.created_by} · ` : ''}
                    {tk.status === 'active' ? relativeExpiry(tk.expires_at) : badge.label.toLowerCase()}
                  </p>
                </div>
                <Badge className={`${badge.className} shrink-0`}>{badge.label}</Badge>
                {tk.status === 'active' && (
                  <div className="flex shrink-0 items-center gap-1.5">
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={() => copy(tk)}>
                      {copiedId === tk.id
                        ? <Check className="h-3.5 w-3.5 text-emerald-600" />
                        : <Copy className="h-3.5 w-3.5" />}
                      {copiedId === tk.id ? 'Copied' : 'Copy link'}
                    </Button>
                    {confirmId === tk.id ? (
                      <Button
                        variant="destructive"
                        size="sm"
                        disabled={revoke.isPending}
                        onClick={() => revoke.mutate(tk.id)}
                      >
                        {revoke.isPending ? 'Revoking…' : 'Confirm revoke'}
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" onClick={() => setConfirmId(tk.id)}>
                        Revoke
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function CaseFieldGrid({ fields, onChange, showRoutingFields, lang }: {
  fields: CaseFields; onChange: (next: CaseFields) => void; showRoutingFields?: boolean; lang: Lang;
}) {
  const set = (key: keyof CaseFields, value: string) => onChange({ ...fields, [key]: value });
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label={t(lang, 'full_name')} required><Input value={fields.patientName} onChange={(e) => set('patientName', e.target.value)} /></Field>
      <div className="grid grid-cols-[0.75fr_1.25fr] gap-2">
        <Field label={t(lang, 'identity_type')}>
          <select className={selectClass} value={fields.identityType} onChange={(e) => set('identityType', e.target.value)}>
            <option value="national_id">{t(lang, 'identity_type_national_id')}</option>
            <option value="iqama">{t(lang, 'identity_type_iqama')}</option>
            <option value="passport">{t(lang, 'identity_type_passport')}</option>
            <option value="other">{t(lang, 'identity_type_other')}</option>
          </select>
        </Field>
        <Field label={t(lang, 'identity_number')}><Input dir="ltr" value={fields.nationalId} onChange={(e) => set('nationalId', e.target.value)} /></Field>
      </div>
      <Field label={t(lang, 'mobile')}><Input dir="ltr" value={fields.phone} onChange={(e) => set('phone', e.target.value)} inputMode="tel" placeholder={t(lang, 'mobile_placeholder')} /></Field>
      <Field label={t(lang, 'nationality')}><Input value={fields.nationality} onChange={(e) => set('nationality', e.target.value)} /></Field>
      <Field label={t(lang, 'payment_method')}>
        <select className={selectClass} value={fields.paymentMethod} onChange={(e) => set('paymentMethod', e.target.value)}>
          <option value="">{t(lang, 'payment_method_placeholder')}</option>
          <option value="cash">{t(lang, 'payment_method_cash')}</option>
          <option value="insurance">{t(lang, 'payment_method_insurance')}</option>
        </select>
      </Field>
      <div className="sm:col-span-2"><Field label={t(lang, 'referral_reason')} required><textarea className={textareaClass} value={fields.cause} onChange={(e) => set('cause', e.target.value)} placeholder={t(lang, 'referral_reason_placeholder')} /></Field></div>
      {showRoutingFields && <>
        <Field label={t(lang, 'referring_provider')}><Input value={fields.sourceProvider} onChange={(e) => set('sourceProvider', e.target.value)} placeholder={t(lang, 'referring_provider_placeholder')} /></Field>
        <Field label={t(lang, 'staff_referral_type')}><Input value={fields.refType} onChange={(e) => set('refType', e.target.value)} placeholder={t(lang, 'staff_referral_type_placeholder')} /></Field>
        <Field label={t(lang, 'staff_requested_specialty')}>
          <SpecialtyPicker
            value={fields.specialty}
            onChange={(description, code) =>
              onChange({ ...fields, specialty: description, specialtyCode: code })}
            placeholder={t(lang, 'staff_specialty_placeholder')}
          />
        </Field>
        <Field label={t(lang, 'staff_bed_type')}><Input value={fields.bedType} onChange={(e) => set('bedType', e.target.value)} /></Field>
      </>}
    </div>
  );
}

function toInitialData(fields: CaseFields): IntakePatientData {
  return {
    name: fields.patientName || undefined, identity_type: fields.identityType,
    national_id: fields.nationalId || null, phone: fields.phone || null,
    nationality: fields.nationality || null, referral_cause: fields.cause || null,
    source_provider: fields.sourceProvider || null, ref_type: fields.refType || null,
    required_specialty: fields.specialty || null,
    required_specialty_code: fields.specialtyCode,
    bed_type: fields.bedType || null,
    payment_method: fields.paymentMethod || null,
  };
}

// Same four typed sections as the patient form, so staff file the documents
// the way the desk reads them (ID / medical / no-insurance / other).
const DOC_SECTIONS: Array<{
  docType: IntakeDocType; title: 'doc_identity_title' | 'doc_medical_title' | 'doc_no_insurance_title' | 'doc_other_title';
  hint: 'doc_identity_hint' | 'doc_medical_hint' | 'doc_no_insurance_hint' | 'doc_other_hint'; multiple: boolean;
}> = [
  { docType: 'identity_document', title: 'doc_identity_title', hint: 'doc_identity_hint', multiple: false },
  { docType: 'medical_report', title: 'doc_medical_title', hint: 'doc_medical_hint', multiple: true },
  { docType: 'no_insurance_proof', title: 'doc_no_insurance_title', hint: 'doc_no_insurance_hint', multiple: false },
  { docType: 'other', title: 'doc_other_title', hint: 'doc_other_hint', multiple: true },
];

function SectionedDocumentPicker({ files, onChange, lang }: {
  files: TaggedFile[]; onChange: (files: TaggedFile[]) => void; lang: Lang;
}) {
  const addFiles = (docType: IntakeDocType, incoming: File[]) => {
    const next = [...files, ...incoming.map((file) => ({ file, docType }))];
    const error = validateIntakeDocuments(next.map((f) => f.file));
    if (error) { toast.error(error); return; }
    onChange(next);
  };
  const remove = (target: TaggedFile) => onChange(files.filter((f) => f !== target));
  return (
    <div className="mt-4">
      <p className="mb-1.5 text-xs font-bold text-muted-foreground">{t(lang, 'documents_title')}</p>
      <p className="mb-2 text-[11px] text-muted-foreground">{t(lang, 'documents_rules')}</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {DOC_SECTIONS.map((section) => {
          const sectionFiles = files.filter((f) => f.docType === section.docType);
          return (
            <div key={section.docType} className="rounded-xl border border-border p-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-xs font-bold">{t(lang, section.title)}</p>
                  <p className="text-[11px] text-muted-foreground">{t(lang, section.hint)}</p>
                </div>
                <label className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-lg border border-dashed border-primary/40 bg-primary/5 px-2.5 py-1.5 text-[11px] font-bold text-primary hover:bg-primary/10">
                  <FileUp className="h-3.5 w-3.5" />
                  {t(lang, section.multiple ? 'add_files' : 'add_file')}
                  <input
                    type="file"
                    multiple={section.multiple}
                    accept="application/pdf,image/jpeg,image/png"
                    className="sr-only"
                    onChange={(e) => { addFiles(section.docType, Array.from(e.target.files ?? [])); e.target.value = ''; }}
                  />
                </label>
              </div>
              {sectionFiles.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {sectionFiles.map((f, i) => (
                    <li key={`${f.file.name}-${i}`} className="flex items-center gap-2 rounded-lg bg-secondary/30 px-2 py-1 text-[11px]">
                      <Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" />
                      <span className="min-w-0 flex-1 truncate" dir="ltr">
                        {f.file.name} ({formatDocumentSize(f.file.size)})
                      </span>
                      <button
                        className="shrink-0 font-bold text-muted-foreground hover:text-red-600"
                        onClick={() => remove(f)}
                        aria-label={t(lang, 'remove_file')}
                      >
                        ✕
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return <label className="block"><span className="mb-1.5 block text-xs font-bold text-muted-foreground">{label}{required && <span className="text-red-500"> *</span>}</span>{children}</label>;
}
